import * as React from "react";
import { ISuggestionObject } from "containers/product-collections/product-collection-cart";
import { RowNode } from "ag-grid-community/dist/lib/entities/rowNode";
import { ILookup, ILookupRawData, IDependency } from "helpers/merchant/lookup/types";
import { AggFuncs } from "constants/product-collection";
import {
    debouncedLookupSizeCurveInternal,
    generateStoreKey,
    transformDataToSuggestions,
} from "helpers/internal-lookups/size-curves-lookup";
import { getServerSideAggregateValueFormatted, renderGroupCount } from "./render-utils";
import { roundPercentage } from "helpers/product-collection";
import { GridApi } from "ag-grid-community";

interface IAssortmentFiles {
    [propName: string]: IAssortmentFilesItem;
}
interface IAssortmentFilesItem {
    string_value: string;
}
interface IProps {
    getSuggestionData: () => ISuggestionObject;
    setSuggestionData: (data: ISuggestionObject, storeKey: string) => void;
    feLookupTypeGetter?: () => (assortmentFields: IAssortmentFiles) => {
        feLookupUrl: string;
        storeKey: string;
        dependency: IDependency;
    };
    internalGetter?: () => (fields: IAssortmentFiles) => {
        interalLookup: any;
        dependency: IDependency;
        lookupPlan?: ILookup;
    };
    updateCombinedDisplayField?: (node: RowNode, lookupValue: ILookupRawData) => void;
    getMissingDependencies?: (data) => string[];
    gridEditable: boolean;
    value: string | number;
    colDef: any;
    column: any;
    data: any;
    node: RowNode;
    api: GridApi;
    context: any;
}

interface IState {
    displayValue: string;
    feLookupUrl?: string;
}

const RENDERER_DISPLAY = "format";
export class SuggestionRenderer extends React.PureComponent<IProps, IState> {
    private _unmounted = false;
    constructor(props) {
        super(props);
        this.state = {
            displayValue: props.data ? props.value : props.valueFormatted || props.value, // Data is empty if the column is grouped therefore no lookup logic
            feLookupUrl: undefined,
        };
    }

    matchInt(num: string) {
        if (num && num.match(/^-{0,1}\d+$/) !== null) {
            return true;
        } else {
            return false;
        }
    }
    componentDidMount() {
        const { data, column, node, colDef, context } = this.props;
        const { isServerSide: serverMode } = colDef.cellRendererParams || {};
        const isServerSide = serverMode || context?.isServerRollup;
        if (isServerSide && (colDef.aggFunc || column?.isAnyFunctionActive())) {
            this.setState({
                displayValue: getServerSideAggregateValueFormatted(this.props as any, ""),
            });
        } else if (column.aggFunc === AggFuncs.COUNT_WITH_PERCENT.toLowerCase() && data) {
            const value = 1;
            const percent = (value * 100) / (node?.parent?.allChildrenCount ?? node?.allChildrenCount);
            this.setState({
                displayValue: `${value} (${isNaN(percent) ? 0 : roundPercentage(percent)}%)`,
            });
        } else {
            if (isServerSide && node.group && !colDef.aggFunc && colDef.cellRenderer !== "agGroupCellRenderer") {
                this.setState({
                    displayValue: "",
                });
            } else if (data) {
                this.processExternalGetter(this.props);
                this.processInternalGetter(this.props);
            }
        }
    }

    processExternalGetter = (props) => {
        const { value, data, getSuggestionData, setSuggestionData, feLookupTypeGetter } = props;
        if (!feLookupTypeGetter && typeof feLookupTypeGetter !== "function") {
            return;
        }
        const { feLookupUrl, storeKey } = feLookupTypeGetter()(data.fields);
        this.setState({ feLookupUrl });
        // parseInt will pass string like "2foo" to 2
        // that's why we need to do a match here.
        const currentKeyInt = this.matchInt(value) ? parseInt(value, 10) : null;
        const options = {
            method: "GET",
        };
        const suggestionData = getSuggestionData() || {};
        if (currentKeyInt !== null) {
            if (suggestionData[storeKey]) {
                const classSuggestion = suggestionData[storeKey].find((suggestion) => suggestion.Code === value);
                if (classSuggestion !== undefined) {
                    this.setState({
                        displayValue: classSuggestion.Value,
                    });
                }
            } else {
                fetch(feLookupUrl, options)
                    .then((response) => response.json())
                    .then((returnSuggestionData) => {
                        if (returnSuggestionData === null || returnSuggestionData.status === false) {
                            return;
                        }
                        if (!this._unmounted) {
                            setSuggestionData(returnSuggestionData, storeKey);
                            const suggestion = returnSuggestionData.find((sug) => sug.Code === value);
                            if (suggestion !== undefined) {
                                this.setState({
                                    displayValue: suggestion.Value,
                                });
                            }
                        }
                    })
                    .catch((_error) => {
                        this.setState({
                            displayValue: props.value,
                        });
                    });
            }
        }
    };

    processInternalGetter = (props) => {
        const {
            value: _value,
            data,
            getSuggestionData,
            setSuggestionData,
            internalGetter,
            getMissingDependencies,
            updateCombinedDisplayField,
            gridEditable,
            api,
        } = props;

        const value = `${_value}`; // Here we could have a number, or a string. We normalize as the logic below only works for strings
        if (!internalGetter && typeof internalGetter !== "function") {
            return;
        }
        const { interalLookup, dependency, lookupPlan } = internalGetter()(data?.fields);
        const missingDependencies = getMissingDependencies(this.props.data);
        const storeKey = generateStoreKey(lookupPlan, dependency, this.props.colDef.field);
        if (missingDependencies.length) {
            // NOTE: no enough info, remove corresponding field.
            gridEditable &&
                updateCombinedDisplayField &&
                updateCombinedDisplayField((this.props as any).node, undefined, api);
            return;
        }
        const suggestionData = getSuggestionData() || {};
        if (suggestionData[storeKey] !== undefined) {
            const newDisplayValue = suggestionData[storeKey].find(
                (item) => item.key === `${value}` || item.value === `${value}` || item[RENDERER_DISPLAY] === `${value}`
            );
            const displayValue = newDisplayValue ? newDisplayValue[RENDERER_DISPLAY] : props.value;

            if (displayValue !== this.state.displayValue) {
                this.setState({
                    displayValue,
                });
            }

            gridEditable &&
                updateCombinedDisplayField &&
                updateCombinedDisplayField((this.props as any).node, newDisplayValue, api);
        } else {
            debouncedLookupSizeCurveInternal(interalLookup, dependency, this.props.colDef.field, storeKey)
                .then((data) => {
                    const { result, isFirst } = data || {};

                    if (!this._unmounted) {
                        if (!result || result.length === 0) {
                            return;
                        }
                        // NOTE: if we have combineFormat, compile with data in line item and return data.
                        const { suggestions } = transformDataToSuggestions(result, this.props.data.fields);
                        const newDisplayValue = suggestions.find(
                            (sug) => sug.key === value || sug.value === value || sug[RENDERER_DISPLAY] === value
                        );

                        const displayValue = newDisplayValue ? newDisplayValue[RENDERER_DISPLAY] : props.value;

                        if (displayValue !== this.state.displayValue) {
                            this.setState({
                                displayValue,
                            });
                        }
                        gridEditable &&
                            updateCombinedDisplayField &&
                            updateCombinedDisplayField((this.props as any).node, newDisplayValue, api);

                        // We don't want to dispatch multiple actions with same payload
                        if (isFirst) {
                            setSuggestionData(suggestions || [], storeKey);
                        }
                    }
                })
                .catch((_error) => {
                    this.setState({
                        displayValue: props.value,
                    });
                });
        }
    };

    componentWillUnmount() {
        this._unmounted = true;
    }

    componentDidUpdate(prevProps) {
        if (!this._unmounted) {
            prevProps.value !== this.props.value && this.refresh();
        }
    }

    refresh = () => {
        this.processExternalGetter(this.props);
        this.processInternalGetter(this.props);
        return true;
    };

    render() {
        const { displayValue } = this.state;
        return (
            <React.Fragment>
                {displayValue}
                {renderGroupCount(this.props as any)}
            </React.Fragment>
        );
    }
}
